import React, { useEffect } from 'react';
import { Play, Loader } from 'lucide-react';
import './SyncCard.css';

/**
 * Tarjeta para mostrar el estado de una sincronización
 */
const SyncCard = ({ 
  title, 
  description, 
  syncType, 
  syncEvent, 
  onExecute, 
  isExecuting,
  executionState 
}) => {
  
  const getButtonText = () => {
    if (executionState) {
      switch (executionState) {
        case 'starting':
          return 'Iniciando...';
        case 'executing':
          return 'Ejecutando...';
        case 'processing':
          return 'Procesando...';
        case 'completing':
          return 'Completando...';
        default:
          return 'Ejecutando...';
      }
    }
    return 'Ejecutar';
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getDuration = () => {
    if (!syncEvent || !syncEvent.start_time) return null;
    
    const start = new Date(syncEvent.start_time);
    const end = syncEvent.end_time ? new Date(syncEvent.end_time) : new Date();
    const durationMs = end - start;
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };
  
  const canExecute = !syncEvent || syncEvent.status !== 'inprogress';
  
  // Debug: Log syncEvent cuando cambia
  useEffect(() => {
    if (syncEvent) {
      console.log(`[SyncCard ${syncType}] syncEvent actualizado:`, syncEvent);
    }
  }, [syncEvent, syncType]);
  
  return (
    <div className="sync-card">
      <div className="sync-card-header">
        <div className="sync-card-title-section">
          <h3 className="sync-card-title">{title}</h3>
          <p className="sync-card-description">{description}</p>
        </div>
        
        <button
          className="sync-execute-btn"
          onClick={onExecute}
          disabled={!canExecute || isExecuting || executionState}
        >
          {(isExecuting || executionState) ? (
            <>
              <Loader size={16} className="rotating" />
              <span>{getButtonText()}</span>
            </>
          ) : (
            <>
              <Play size={16} />
              <span>Ejecutar</span>
            </>
          )}
        </button>
      </div>
      
      <div className="sync-card-body">
        <div className="sync-info-row">
          <div className="sync-info-item">
            <span className="sync-info-label">Última ejecución:</span>
            <span className="sync-info-value">
              {formatDate(syncEvent?.start_time)}
            </span>
          </div>
          
          {syncEvent && syncEvent.end_time && (
            <div className="sync-info-item">
              <span className="sync-info-label">Duración:</span>
              <span className="sync-info-value">{getDuration()}</span>
            </div>
          )}
        </div>
        
        {syncEvent && syncEvent.result && (
          <div className="sync-results">
            <h4 className="sync-results-title">Resultado:</h4>
            <div className="sync-results-grid">
              <div className="sync-result-item">
                <span className="sync-result-label">Registros leídos:</span>
                <span className="sync-result-value">
                  {syncEvent.result.records_read || 0}
                </span>
              </div>
              <div className="sync-result-item">
                <span className="sync-result-label">Creados:</span>
                <span className="sync-result-value sync-result-created">
                  {syncEvent.result.clientes_created || 
                   syncEvent.result.products_created || 
                   syncEvent.result.inventory_created || 0}
                </span>
              </div>
              <div className="sync-result-item">
                <span className="sync-result-label">Actualizados:</span>
                <span className="sync-result-value sync-result-updated">
                  {syncEvent.result.clientes_updated || 
                   syncEvent.result.products_updated || 
                   syncEvent.result.inventory_updated || 0}
                </span>
              </div>
              {syncEvent.result.skipped > 0 && (
                <div className="sync-result-item">
                  <span className="sync-result-label">Omitidos:</span>
                  <span className="sync-result-value">
                    {syncEvent.result.skipped}
                  </span>
                </div>
              )}
              {syncEvent.result.errors_count > 0 && (
                <div className="sync-result-item">
                  <span className="sync-result-label">Errores:</span>
                  <span className="sync-result-value sync-result-error">
                    {syncEvent.result.errors_count}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {syncEvent && syncEvent.error && (
          <div className="sync-error">
            <h4 className="sync-error-title">Error:</h4>
            <p className="sync-error-message">{syncEvent.error}</p>
          </div>
        )}
        
        {syncEvent && syncEvent.status === 'inprogress' && (
          <div className="sync-progress">
            <div className="sync-progress-bar">
              <div className="sync-progress-bar-fill"></div>
            </div>
            <p className="sync-progress-text">
              Sincronización en progreso... Iniciada hace {getDuration()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncCard;
